import React, { forwardRef, useState } from 'react';

import { Select } from '@aline/common';
import { Badge, DialogActions, DialogContent, Typography, useTheme } from '@mui/material';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import {
  gridFilterModelSelector,
  GridLogicOperator,
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  gridSortModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { ListFilter } from 'lucide-react';
import {
  AlineButton,
  AlineDialog,
  AlineTooltip,
  DropdownSelectButton,
  SelectButton,
  useFilterFuncs,
} from 'src/components';
import { AiRobotIcon } from 'src/components/icons/AiRobotIcon';
import { FavoriteStarIcon } from 'src/components/icons/FavoriteStarIcon';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { useReportParams } from 'src/helpers';
import {
  getVisibleDocumentIds,
  useFiltersAnchorRef,
  useGetExtractionQuestions,
  useGetSelectedReportRows,
  useGetVisibleQuestionColumns,
  useRunExtractionJobsForDocuments,
  useStartReportExtraction,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  baseButton: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  buttonText: {
    fontWeight: 600,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  dialogPaper: {
    width: 640,
  },
  filtersButtonDialog: {
    borderRadius: 8,
    boxSizing: 'border-box',
    height: 32,
    minHeight: 32,
    padding: theme.spacing(1),
  },
  filtersButtonDialogLabel: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  filtersButtonDialogText: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 600,
  },
  submitButton: {
    marginLeft: theme.spacing(1),
  },
}));

export const FiltersToolbarButton = forwardRef(function FiltersToolbarButton(
  _props: Record<string, never> | undefined,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const preferencePanelState = useGridSelector(apiRef, gridPreferencePanelStateSelector);
  const isFiltersOpen =
    preferencePanelState.open &&
    preferencePanelState.openedPanelValue === GridPreferencePanelsValue.filters;
  /**
   * Register this button as the anchor for the DataGrid's filter panel (if the
   * parent list page wrapped the grid in a FiltersAnchorProvider). Composed
   * with the forwarded ref so external refs still work.
   */
  const setFiltersAnchor = useFiltersAnchorRef();
  const composedRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      setFiltersAnchor(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref, setFiltersAnchor]
  );

  function handlePreferenceClick(value: GridPreferencePanelsValue): () => void {
    return function (): void {
      if (preferencePanelState.openedPanelValue === value) return apiRef.current.hidePreferences();

      apiRef.current.showPreferences(value);
    };
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (preferencePanelState.open) event.stopPropagation();
  };

  return (
    <Badge
      badgeContent={filterModel.items.length}
      color="primary"
      sx={{ '& .MuiBadge-badge': { right: 2, top: 2 } }}
    >
      <DropdownSelectButton
        ref={composedRef}
        buttonVariant="default"
        data-cy={Select.components.filters.button}
        icon={<ListFilter size={18} />}
        label="Filters"
        selected={isFiltersOpen}
        variant="outlined"
        onClick={handlePreferenceClick(GridPreferencePanelsValue.filters)}
        onPointerUp={handlePointerUp}
      />
    </Badge>
  );
});

export function FavoritesToolbarButton({
  className,
}: React.HTMLAttributes<HTMLElement>): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const filterModel = gridFilterModelSelector(apiRef);
  const { isMyFavoritesActive } = useFavoritesFilter(apiRef);
  const { deleteFilter, upsertFilter } = useFilterFuncs();

  async function handleFavoritesClick(): Promise<void> {
    filterModel.items
      .filter(item => item.field === Apollo.ReportColumnFileField.RequesterFavorited)
      .forEach(item => deleteFilter(item));

    if (isMyFavoritesActive) return;

    apiRef.current.setFilterLogicOperator(GridLogicOperator.And);
    upsertFilter({
      field: Apollo.ReportColumnFileField.RequesterFavorited,
      id: uuid4(),
      operator: 'is',
      value: 'true',
    });
  }

  return (
    <SelectButton
      buttonColor={theme.appColors.accent5}
      className={className}
      labelClass={classes.baseButton}
      selected={isMyFavoritesActive}
      variant="outlined"
      onClick={handleFavoritesClick}
    >
      <FavoriteStarIcon
        fill={isMyFavoritesActive ? theme.appColors.accent5 : theme.appColors.rule}
      />
      <Typography color="inherit" variant="body1">
        Favorited Files
      </Typography>
    </SelectButton>
  );
}

export function useFavoritesFilter(apiRef: React.RefObject<GridApiPremium | null>) {
  // @ts-expect-error MUI v9: useGridApiRef returns RefObject<X | null> but useGridSelector's parameter type is RefObject<X> (non-null inner). Safe in practice because this hook only runs once the grid has mounted and apiRef.current is non-null.
  const sortModel = useGridSelector(apiRef, gridSortModelSelector);
  // @ts-expect-error MUI v9 nullable-vs-non-null ref-object mismatch (see above).
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const favoritesFilterItem =
    filterModel.items.find(
      item => item.field === Apollo.ReportColumnFileField.RequesterFavorited
    ) ?? null;
  const isAndOperatorActive =
    !filterModel.logicOperator || filterModel.logicOperator === GridLogicOperator.And;

  return {
    favoritesFilterItem,
    favoritesFilterValue: favoritesFilterItem
      ? favoritesFilterItem.value === apiRef.current?.getLocaleText('filterValueTrue')
      : null,
    isMyFavoritesActive:
      isAndOperatorActive &&
      favoritesFilterItem?.value === apiRef.current?.getLocaleText('filterValueTrue'),
    isSortedByFavorites: sortModel.some(
      item => item.field === Apollo.ReportColumnFileField.RequesterFavorited
    ),
  };
}

interface AiExtractButtonProps {
  rowCount: number;
}

export function AlineAiExtractButton({ rowCount }: AiExtractButtonProps) {
  const { classes } = useStyles();
  const { reportId } = useReportParams();
  const { visibleQuestionColumns } = useGetVisibleQuestionColumns();
  const { apiRef, selectedReportRows } = useGetSelectedReportRows();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { extractionQuestions } = useGetExtractionQuestions({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { loading: startingExtractionJobs, startExtractionJobsForDocuments } =
    useRunExtractionJobsForDocuments();
  const { loading: startingReportExtraction, startReportExtraction } = useStartReportExtraction();
  const primaryDisabledReason = !extractionQuestions.length
    ? 'Add questions to your account to run Aline AI'
    : '';
  const documentsString = selectedReportRows.length
    ? `${selectedReportRows.length} selected ${selectedReportRows.length === 1 ? 'document' : 'documents'}`
    : `${rowCount} ${rowCount === 1 ? 'document' : 'documents'}`;

  return (
    <>
      <SelectButton labelClass={classes.baseButton} onClick={() => setOpenDialog(!openDialog)}>
        <AiRobotIcon height={24} width={24} />
        <Typography>Run Aline AI</Typography>
      </SelectButton>
      <AlineDialog
        classes={{ paper: classes.dialogPaper }}
        open={openDialog}
        title="Run AI questions?"
        onClose={() => setOpenDialog(false)}
      >
        <DialogContent className={classes.dialogContent}>
          <Typography>
            {`Are you sure you want to run all questions for ${documentsString}? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <AlineButton color="primary" variant="outlined" onClick={() => setOpenDialog(false)}>
            Cancel
          </AlineButton>
          <AlineTooltip arrow placement="top" title={primaryDisabledReason}>
            <div className={classes.submitButton}>
              <AlineButton
                color="primary"
                disabled={Boolean(primaryDisabledReason)}
                loading={startingExtractionJobs || startingReportExtraction}
                variant="contained"
                onClick={async () => {
                  if (!selectedReportRows.length) {
                    // Run AI extraction on the entire (filtered) report.
                    await startReportExtraction({
                      variables: {
                        input: {
                          filterModel,
                          filterTimeZone,
                          reportId,
                          visibleDocumentIds: getVisibleDocumentIds(apiRef.current),
                        },
                      },
                    });
                    setOpenDialog(false);
                    return;
                  }

                  /**
                   * `row.id` is the grid row ID (composite in grouped/tree mode), `row.documentId`
                   * is always the real document UUID. Mutation needs UUIDs; selectRows needs grid IDs.
                   */
                  const selectedRowIds = selectedReportRows.map(row => row.id);
                  const documentIds = selectedReportRows.map(row => row.documentId);

                  await startExtractionJobsForDocuments({
                    variables: {
                      input: {
                        documentIds,
                        questionIds: visibleQuestionColumns.map(col => col.field),
                      },
                    },
                  });
                  setOpenDialog(false);
                  // Clear the selection only after the run is queued, so a failed run keeps it.
                  apiRef.current.selectRows(selectedRowIds, false);
                }}
              >
                Run
              </AlineButton>
            </div>
          </AlineTooltip>
        </DialogActions>
      </AlineDialog>
    </>
  );
}
